import React, { useContext, useEffect, useState } from "react";
import { getModifiedScrollHeight } from "../../../../../utils/UtilFunctions";
import { useNavigate } from "react-router";

import { Title } from "../../../../../stories/Title/Title";
import Input from "../../../../../stories/Input/Input";
import {
  empType,
  LicenseTypes,
  Operation,
} from "../../../../../utils/Enum.types";
import {
  debouncedDelay,
  emptyMessage,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../utils/constants";
import { Button } from "../../../../../stories/Button/Button";
import {
  GridAlignment,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";

import useAssetTableJson from "../../../../../json/useAssetTableJson";
import { TableHeaderProps } from "../../../../../utils/types";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GetEmp } from "./../queries/queries";

import {
  EmpMasterEdge,
  EmpMasterOrderField,
  EmpQueryType,
  GetEmpResult,
  GetEmpVars,
} from "./../queries/types";

import useUrlParams from "../../../../../customHooks/useUrlParams";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { AppContext } from "../../../../../context/context";
import { TABLE_ROW_HEIGHT } from "../../../../../styles/Variables";
import { StyledDatagrid } from "../../../../../styles/DatagridStyles";
import Navbar from "../../../../common/layout/Navbar";
import LoadingModal from "../../../../pages/LoadingModal";
import EmpTypeSelect from "../../../../../customComponents/EmpTypeSelect";
import { Checkbox, TextField } from "@mui/material";
import { UpdateEmpLoginStatus } from "./queries/mutations";
import {
  UpdateEmpLoginStatusResult,
  UpdateEmpLoginStatusVar,
} from "./queries/types";
import useLoggedInUserDetails from "../../../../../customHooks/useLoggedInUserDetails";
import MessageModal from "../../../../../customComponents/MessageModal";
import {
  labelClasses,
  LabeledAutocomplete,
} from "../../../../../styles/AutocompleteStyles";
import { useDebounce } from "../../../../../customHooks/useDebounce";

export interface ModalType {
  operation: Operation;
  id: number;
  flag: boolean;
}

const EnableUserLogin = () => {
  const navigate = useNavigate();
  const { Users } = useAssetTableJson();

  const { state, dispatch } = useContext(AppContext);
  const { customer_id, company_id } = useUrlParams();
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [employees, setEmployees] = useState<EmpMasterEdge[]>([]);
  const [rows, setRows] = useState<GridValidRowModel[]>([]);
  const [selectedEmpType, setSelectedEmpType] = useState("");
  const [empQueryType, setEmpQueryType] = useState(EmpQueryType.ALL);
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [message, setMessage] = useState(emptyMessage);
  const { user_details } = useLoggedInUserDetails();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [loginStatus, setLoginStatus] = useState("");
  const [selectStatus, setSelectStatus] = useState("");

  const handleCheckboxChange = (id: number) => {
    setSelectedItems(
      (prevSelected) =>
        prevSelected.includes(id)
          ? prevSelected.filter((itemId) => itemId !== id) // Remove if already selected
          : [...prevSelected, id] // Add if not selected
    );
  };

  const [GetEmployees, { data, loading, fetchMore }] = useLazyQuery<
    GetEmpResult,
    GetEmpVars
  >(GetEmp);

  const [updateEmpLoginStatus, { loading: updateLoading }] = useMutation<
    UpdateEmpLoginStatusResult,
    UpdateEmpLoginStatusVar
  >(UpdateEmpLoginStatus, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });
  const dynamicHeaders: TableHeaderProps[] = Users.UserLoginState.map(
    (header) => ({
      headerName: header.headerName,
      className: header.cellClassName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (header.field === "id") {
          return (
            <>
              <Checkbox
                value={params.row}
                checked={selectedItems.includes(params.row.user_id)}
                onChange={() => handleCheckboxChange(params.row.user_id)}
                inputProps={{ "aria-label": "controlled" }}
              />
            </>
          );
        }

        if (header.field === "isLoginCreated") {
          return <>{params.row.isLoginCreated ? "Active" : "InActive"}</>;
        }
        return params.value;
      },
    })
  );
  const columns: GridColDef[] = [...dynamicHeaders];

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetEmp.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newEmp) => {
          const filteredStudent = employees.find(
            (employee) => employee.node.id === newEmp.node.id
          );
          if (filteredStudent) {
            return {
              ...newEmp,
              node: {
                ...newEmp.node,
              },
            };
          }
          return newEmp;
        });
        setEmployees(updatedNewData);
        setRows(
          updatedNewData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            mobile: node.mobile,
            email: node.email,
            user_type: node.empType,
            user_id: node.id,
            customerName: node.customer.name,
            isLoginCreated: node.isLoginCreated,
          }))
        );
      } else {
        setEmployees(newData);

        setRows(
          newData.map(({ node }, index) => ({
            id: index + 1,
            name: node.name,
            mobile: node.mobile,
            email: node.email,
            user_type: node.empType,
            user_id: node.id,
            customerName: node.customer.name,
            isLoginCreated: node.isLoginCreated,
          }))
        );
      }
      setEndCursor(data.GetEmp.pageInfo.endCursor);
    } // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetEmp.edges;
              const pageInfo = fetchMoreResult.GetEmp.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetEmp.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetEmp: {
                  edges: [...employees, ...newEdges],
                  pageInfo,
                  totalCount: data ? data.GetEmp.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  useEffect(() => {
    const fetchData = async () => {
      try {
     
        if (customer_id) {
          GetEmployees({
            variables: {
              after: null,
              customer_id,
              first: ROWS_PER_PAGE,
              input: {
                queryType: empQueryType,
                empType: selectedEmpType
                  ? [(selectedEmpType as empType)]
                  : [empType.DCP],
                companyID: state.companyId ? state.companyId : 0,
                ldgrID: 0,
                siteID: state.subSiteId ? state.subSiteId : 0,
                subsidiaryID: state.subsidiaryId ? state.subsidiaryId : 0,
              },
              orderBy: {
                direction: OrderDirection.ASC,
                field: EmpMasterOrderField.NAME,
              },
              
              name: debouncedSearch,  
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [
    customer_id,
    GetEmployees,
    debouncedSearch,
    state.companyId,
    state.subSiteId,
    state.subsidiaryId,
    selectedEmpType,
    empQueryType,
  ]);

  const handleSubmit = async () => {
    if (customer_id) {
      updateEmpLoginStatus({
        variables: {
          
          user_details,
          customerID: customer_id,
          empIds: selectedItems,
          loginStatus: loginStatus === "Active" ? true : false,
          selectAll: false,
        },
        refetchQueries: [
          {
            query: GetEmp,
            variables: {
              after: null,
              customer_id,
              first: ROWS_PER_PAGE,
              input: {
                queryType: empQueryType,
                empType: selectedEmpType
                  ? [(selectedEmpType as empType)]
                  : [empType.DCP],
                companyID: state.companyId ? state.companyId : 0,
                ldgrID: 0,
                siteID: state.subSiteId ? state.subSiteId : 0,
                subsidiaryID: state.subsidiaryId ? state.subsidiaryId : 0,
              },
              orderBy: {
                direction: OrderDirection.ASC,
                field: EmpMasterOrderField.NAME,
              },
              
              name: debouncedSearch,
            },
          },
        ],
      }).then(({ data }) => {
        if (data && data.UpdateEmpLoginStatus) {
          setMessage({
            flag: true,
            message: "Updated successfully",
            operation: Operation.UPDATE,
          });
          setSelectedItems([]);
          setLoginStatus("");
        }
      });
    }
  };

  const handleClose = () => {
    setMessage({
      message: "",
      flag: false,
      operation: Operation.NONE,
    });
  };

  return (
    <>
      <Navbar NavType={LicenseTypes.CUSTOMER_USERS} />

      <Title>{rows ? rows[0]?.customerName : "Customer Users"}</Title>
      <div className="cust_users">
        <div className="row g-0 cust_users--filters">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search ..."
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchData(e.target.value)
              }
            />
          </div>
          {/* <div className="col-2">
            <CompanySelect
              label="Company Name"
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_COMPANY_ID,
                    payload: {
                      companyId: newValue.value,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_COMPANY_ID,
                    payload: {
                      companyId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                }
              }}
              onKeyDown={() => {}}
              value={state.companyId ? state.companyId : 0}
            />
          </div>
          <div className="col-2">
            <SubsidiarySelect
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: newValue.node.id,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                }
              }}
              label={"Subsidiary"}
              companyID={state.companyId ? state.companyId : 0}
              onKeyDown={emptyFunc}
              value={state.subsidiaryId ? state.subsidiaryId : 0}
            />
          </div>
          <div className="col-2">
            <SiteSelect
              label="Site Name"
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: newValue.value,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                }
              }}
              companyId={state.companyId ? state.companyId : 0}
              subsidiaryId={state.subsidiaryId ? state.subsidiaryId : 0}
              onKeyDown={() => {}}
              value={state.subSiteId ? state.subSiteId : 0}
            />
          </div> */}
          <div className="col-2">
            <EmpTypeSelect
              value={selectedEmpType}
              onChange={(newValue) => {
                if (newValue) {
                  setEmpQueryType(EmpQueryType.BY_TYPE);
                  setSelectedEmpType(newValue as empType);
                } else {
                  setEmpQueryType(EmpQueryType.ALL);
                  setSelectedEmpType("");
                }
              }}
              label="Type"
            />
          </div>
          <div className="col-2">
            <LabeledAutocomplete
              className={labelClasses.inputRoot}
              options={["Active", "InActive"]}
              value={selectStatus}
              openOnFocus
              forcePopupIcon
              onChange={(e, newValue) => {
                if (newValue) {
                  setEmpQueryType(
                    newValue === "Active"
                      ? EmpQueryType.LOGIN_STATUS_ACTIVE
                      : EmpQueryType.LOGIN_STATUS_INACTIVE
                  );
                } else {
                  setEmpQueryType(EmpQueryType.ALL);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label="Filter by Status"
                  className={labelClasses.formControlRoot}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              )}
            />
          </div>
          
        </div>
        <div className="cust_users__tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            rowHeight={TABLE_ROW_HEIGHT}
            disableRowSelectionOnClick
            hideFooter
          />
        </div>
        <div className="row align-items-center">
        <div className="col-2">
          <LabeledAutocomplete
              className={labelClasses.inputRoot}
              options={["Active", "InActive"]}
              value={loginStatus}
              openOnFocus
              forcePopupIcon
              onChange={(e, newValue) => {
                if (newValue) {
                  setLoginStatus(newValue as string);
                } else {
                  setLoginStatus("");
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={"Change Status"}
                  className={labelClasses.formControlRoot}
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                />
              )}
            />
          </div>
          <div className="col">         
            <Button
              mode="save"
              onClick={() => {
                handleSubmit();
              }}
              disabled={loginStatus && selectedItems.length ? false : true}
            />

            <Button mode={"back"} onClick={() => navigate(-1)} />
          </div>
          <div className="col flex-end">
            <div className="overall-total-count">
              Total Users : <b>{data?.GetEmp.totalCount}</b>
            </div>
          </div>
        </div>
      </div>
      <MessageModal
        modalFlag={message.flag!}
        value={message.message!}
        handleClose={handleClose}
        operation={message.operation!}
      />

      <LoadingModal flag={(!searchData && loading) || updateLoading} />
    </>
  );
};

export default EnableUserLogin;
